import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneInput from '@/components/PhoneInput';
import { ArrowRight, KeyRound } from 'lucide-react';

const inputCls = 'rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]';
const labelCls = 'uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block';
const ctaCls = 'w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed';

export default function ForgotPasswordPage() {
  const { forgotPassword, verifyResetOTP, resetPassword, resendOTP } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [channel, setChannel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhone = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await forgotPassword(phone);
      setChannel(res?.data?.channel || 'sms');
      setStep('otp');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await verifyResetOTP(phone, otp);
      setResetToken(res?.data?.reset_token);
      setStep('new');
    } catch (err) {
      setError(err?.response?.data?.error || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await resetPassword(resetToken, newPassword);
      navigate('/login');
    } catch (err) {
      setError(err?.response?.data?.error || 'Reset failed');
    } finally { setLoading(false); }
  };

  const subtitle = {
    phone: 'Enter your registered mobile number',
    otp: `OTP sent via ${(channel || 'sms').toUpperCase()} to ${phone}`,
    new: 'Choose a new password',
  }[step];

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-16 bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4">Recovery</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 48 }}>Reset password</h1>
          <p className="mt-4 text-ink/70" style={{ fontSize: 15 }}>{subtitle}</p>
        </div>

        {step === 'phone' && (
          <form onSubmit={handlePhone} className="space-y-6" data-testid="phone-form">
            <div>
              <Label htmlFor="phone" className={labelCls}>Mobile Number</Label>
              <PhoneInput id="phone" value={phone} onChange={setPhone} required data-testid="phone-input" className={inputCls} />
            </div>
            {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className={ctaCls} style={{ fontSize: 13 }} data-testid="send-reset-otp-btn">
              {loading ? 'Sending…' : (<>Send OTP <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <p className="text-center pt-2" style={{ fontSize: 14 }}>
              <Link to="/login" className="text-ink hover:text-gold underline underline-offset-4">← Back to sign in</Link>
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerify} className="space-y-6" data-testid="otp-form">
            <div>
              <Label htmlFor="otp" className={labelCls}>One-time Password</Label>
              <Input id="otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required maxLength={6} placeholder="6-digit code" data-testid="otp-input"
                className={`${inputCls} text-center text-[42px] leading-none tracking-[0.4em] font-display font-semibold py-2`} />
            </div>
            {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className={ctaCls} style={{ fontSize: 13 }} data-testid="verify-reset-otp-btn">
              {loading ? 'Verifying…' : (<>Verify OTP <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep('phone')} className="text-ink-muted hover:text-ink uppercase tracking-luxe text-[12px] font-medium" data-testid="back-btn">← Back</button>
              <button type="button" onClick={() => resendOTP(phone, 'reset_password').then(r => setChannel(r?.data?.channel || 'sms'))} className="text-ink hover:text-gold underline underline-offset-4 uppercase tracking-luxe text-[12px] font-medium" data-testid="resend-otp-btn">Resend OTP</button>
            </div>
          </form>
        )}

        {step === 'new' && (
          <form onSubmit={handleReset} className="space-y-6" data-testid="new-password-form">
            <div>
              <Label htmlFor="np" className={labelCls}>New Password</Label>
              <Input id="np" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required minLength={6} placeholder="At least 6 characters" data-testid="new-password-input" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="cp" className={labelCls}>Confirm New Password</Label>
              <Input id="cp" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={6} data-testid="confirm-new-password-input" className={inputCls} />
            </div>
            {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className={ctaCls} style={{ fontSize: 13 }} data-testid="reset-password-btn">
              <KeyRound className="w-4 h-4" />{loading ? 'Resetting…' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
